import {
  Invitation,
  InvitationStatusEnum,
  OnboardingStatusEnum,
  RoleEnum,
  User,
} from '@sr-sdks/api-sdk-axios';
import { sonner } from '@sr/ui-library';
import { QueryClient } from '@tanstack/react-query';
import {
  invalidateUserRoleQueries,
  updateUserRole,
} from '../../api/permissions/userRoleApi';
import { userService } from '../../reducers/permissions/userSlice';
import { AppDispatch } from '../../stateStore';

const { toast } = sonner;

interface UpdateOnboardingStatusParams {
  dispatch: AppDispatch;
  onboardingStatus: OnboardingStatusEnum;
  queryClient: QueryClient;
  user: User;
  workspaceId: string;
}

export const updateWorkspaceOwnerOnboardingStatus = async ({
  dispatch,
  onboardingStatus,
  queryClient,
  user,
  workspaceId,
}: UpdateOnboardingStatusParams): Promise<void> => {
  const workspaceOwnerRole = user.roles?.find(
    (role) =>
      role.workspaceId === workspaceId &&
      role.roleKey === RoleEnum.WorkspaceOwner,
  );

  if (workspaceOwnerRole) {
    const currentStatus = workspaceOwnerRole.onboardingStatus;

    if (currentStatus === OnboardingStatusEnum.Completed) {
      return;
    }

    // The plain function, not the mutation hook: this helper runs outside a
    // component, so it takes the caller's `queryClient` for the invalidation.
    await updateUserRole({
      id: workspaceOwnerRole._id,
      updateUserRoleDto: {
        onboardingStatus,
      },
    });
    await invalidateUserRoleQueries(queryClient);

    // Re-fetch user after role update - fixes onboarding loop APP-1000
    await dispatch(userService.findMe());
  }
};

/**
 * Whether `email` is the address the invitation was sent to. Only the invitee
 * may go through onboarding for an invitation (APP-2441); anyone else signed in
 * is shown the wrong-account screen instead. Trimmed and case-insensitive;
 * unknown email never matches.
 */
export const isInvitationForEmail = (
  invitation: Invitation,
  email: string | undefined,
) =>
  !!email &&
  invitation.emailAddress.trim().toLowerCase() === email.trim().toLowerCase();

/**
 * Whether the signed-in account is resuming an invitation it has already
 * accepted — e.g. they saved the profile step, closed the tab and reopened the
 * invitation link (APP-2441). Accepting a second time is rejected by the
 * backend, so the profile step continues without it.
 *
 * Only the invitee counts: anyone else signed in still goes through accept, so
 * the backend can reject the email mismatch rather than the app sending them
 * on to a workspace they have no role in.
 *
 * Takes the auth session's email, the one the account guard and invitation
 * page check, so all three agree on who the invitee is; the stored profile's
 * address can differ from it.
 */
export const isInvitationAcceptedByEmail = (
  invitation: Invitation | undefined,
  email: string | undefined,
) =>
  invitation?.invitationStatus === InvitationStatusEnum.Accepted &&
  isInvitationForEmail(invitation, email);

/**
 * `jane@example.com` → `j•••@example.com`. Enough for the invitee to recognise
 * their own address without showing someone else's in full to whoever holds a
 * forwarded invitation link.
 */
export const maskEmailAddress = (email: string) => {
  const [localPart, domain] = email.split('@');

  if (!localPart || !domain) return '•••';

  return `${localPart[0]}•••@${domain}`;
};

/**
 * Teammates stop at the profile. Any owner role keeps workspace setup, or the
 * workspace is left without its details (APP-2386).
 */
export const isTeammateOnlyInvitation = (userRoles?: RoleEnum[]) =>
  Boolean(
    userRoles?.some(
      (role) =>
        role === RoleEnum.WorkspaceAdmin || role === RoleEnum.WorkspaceUser,
    ) && !userRoles.includes(RoleEnum.WorkspaceOwner),
  );

/**
 * The message both onboarding steps raise when Save & Continue cannot proceed.
 * Shared so the profile and workspace steps cannot drift apart on copy the
 * user reads as one flow.
 */
export const showOnboardingInvalidFormToast = () => {
  toast.error('Unable to Continue', {
    description: 'Please check all required forms are filled out.',
  });
};
